import React, { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'

import type { OptionItem } from '@wartek-id/select'

import {
  getMonthDateRange,
  KinerjaHarianLogs,
  BannerPengembangan,
} from 'app/KinerjaHarian/components/logs'
import { MONTHS } from 'app/KinerjaHarian/constants'
import { Header } from 'app/KinerjaShared/components/Header'
import { SurveyDialog } from 'app/KinerjaShared/components/SurveyDialog'
import { useKemudahanSurveyDialog } from 'app/KinerjaShared/hooks/use-kemudahan-survey-dialog'
import { useKemudahanSurveyTrackEvent } from 'app/KinerjaShared/hooks/use-kemudahan-survey-track-event'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { useSafeBackNavigation } from 'app/KinerjaShared/hooks/useSafeBackNavigation'
import { useKinerjaHarianTrackEvent } from 'app/KinerjaHarian/hooks/useKinerjaHarianTrackEvent'
import { capitalizeFirstLetter } from 'utils/text'

import type { KinerjaHarianLog, KinerjaHarianView } from 'types/kinerja-harian'
import type { Actor } from 'app/KinerjaShared/hooks/use-kemudahan-survey-track-event'

export interface KinerjaHarianListPageRoutes {
  base: string
  list: string
  add: string
  edit: (id: string) => string
}

export interface KinerjaHarianListPageProps {
  isLoading?: boolean
  logs: KinerjaHarianLog[]
  routes: KinerjaHarianListPageRoutes
  trackerData: { tipe: Actor }
  onDelete: (entryDate: string) => void
  onDateChange: (startDate: string, endDate: string) => void
}

export function KinerjaHarianListPage({
  isLoading,
  logs,
  routes,
  trackerData,
  onDelete,
  onDateChange,
}: KinerjaHarianListPageProps) {
  const router = useRouter()
  const { periodYear } = usePeriodContext()
  const { trackEvent } = useKinerjaHarianTrackEvent()

  const {
    isOpen: isSurveyOpen,
    isSubmitted: isSurveySubmitted,
    open: openSurveyDialog,
    close: closeSurveyDialog,
    markAsSubmitted: markSurveyAsSubmitted,
  } = useKemudahanSurveyDialog({
    type: 'kinerja-harian',
    additionalKey: trackerData.tipe,
  })

  const { trackEvent: trackSurveyEvent } = useKemudahanSurveyTrackEvent()

  const handleSubmitSurvey = (selected) => {
    trackSurveyEvent({
      actor: trackerData.tipe,
      eventAction: 'submit',
      eventCategory: 'survey_kemudahan_kinerja_harian',
      customDimensions: {
        likert_value: String(selected.value),
        likert_label: selected.label,
      },
    })
  }

  const handleOpenSurveyDialog = () => {
    openSurveyDialog()
    trackSurveyEvent({
      actor: trackerData.tipe,
      eventAction: 'view',
      eventCategory: 'survey_kemudahan_kinerja_harian',
    })
  }

  const handleCloseSurveyDialog = () => {
    closeSurveyDialog()
  }

  const handleOpenSurveyForm = () => {
    trackSurveyEvent({
      actor: trackerData.tipe,
      eventAction: 'view_gform',
      eventCategory: 'survey_kemudahan_kinerja_harian',
    })
    markSurveyAsSubmitted()
  }

  // -----------------------------------------
  // Handle guard back navigation for unsaved changes
  // -----------------------------------------
  const { handleBack } = useSafeBackNavigation({
    fallbackRoute: routes.base,
  })

  // ----------------------------------------
  //  Handle date range selection
  // ----------------------------------------
  const selectedYear = periodYear ?? new Date().getFullYear()
  const [selectedMonthOption, setSelectedMonthOption] = useState<string>(() =>
    getDefaultSelectedMonthOption(selectedYear)
  )

  // Derive date range from the current month option and notify the caller.
  const { startDate, endDate } = useMemo(
    () => getMonthDateRange(selectedMonthOption, selectedYear),
    [selectedMonthOption, selectedYear]
  )

  useEffect(() => {
    onDateChange(startDate, endDate)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate])

  const handleMonthClick = () => {
    trackEvent({
      activityType: 'click_dropdown_month',
      screenName: 'harian',
      additionalParams: {
        clicked_text: 'month',
      },
    })
  }

  const handleMonthChange = (option: OptionItem) => {
    const isSameMonth = selectedMonthOption === option.option
    setSelectedMonthOption(option.option)

    trackEvent({
      activityType: 'click_dropdown_month',
      screenName: 'harian',
      additionalParams: {
        clicked_text: isSameMonth
          ? 'NULL'
          : capitalizeFirstLetter((option.option ?? '').replace('-', ' ')),
      },
    })
  }

  const handleViewChange = (view: KinerjaHarianView) => {
    trackEvent({
      activityType: 'click_change_view',
      screenName: 'harian',
      additionalParams: {
        clicked_text: view === 'calendar' ? 'Kalender' : 'Daftar',
      },
    })
  }

  // Reset to the default month when the period year changes.
  useEffect(() => {
    setSelectedMonthOption(getDefaultSelectedMonthOption(selectedYear))
  }, [selectedYear])

  // Sync month from router query params (e.g. redirected back from add/edit).
  useEffect(() => {
    if (!router.isReady) return
    if (!router.query.month && !router.query.saved) return

    if (router.query.month) {
      const monthOption = getDefaultSelectedMonthOption(
        selectedYear,
        new Date(selectedYear, parseInt(router.query.month as string) - 1, 1)
      )

      setSelectedMonthOption(monthOption)
    }

    // Show survey dialog after save, if not submitted yet
    if (router.query.saved === 'true' && !isSurveySubmitted) {
      handleOpenSurveyDialog()
    }

    // remove query params after syncing with state to prevent confusion
    router.replace(
      {
        pathname: routes.list,
      },
      undefined,
      { shallow: true }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router, selectedYear, routes.list, isSurveySubmitted, trackerData.tipe])

  React.useEffect(() => {
    trackEvent({
      activityType: 'page_view',
      screenName: 'harian',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <main className="max-w-5xl py-8 px-6 mx-auto space-y-8">
      <Header title="Kinerja Harian" onBack={handleBack} />
      <BannerPengembangan />
      <KinerjaHarianLogs
        isLoading={isLoading}
        harianLogs={logs}
        selectedMonthOption={selectedMonthOption}
        selectedYear={selectedYear}
        onCreate={() => {
          trackEvent({
            activityType: 'click_create',
            screenName: 'harian',
          })
          router.push(routes.add)
        }}
        onDelete={onDelete}
        onEdit={(itemId: string) => {
          trackEvent({
            activityType: 'click_edit',
            screenName: 'harian',
            additionalParams: {
              edit_type: 'edit',
            },
          })
          router.push(routes.edit(itemId))
        }}
        onMonthClick={handleMonthClick}
        onMonthChange={handleMonthChange}
        onViewChange={handleViewChange}
      />

      <SurveyDialog
        autoRedirectSeconds={5}
        formContent={{
          question:
            'Dalam skala 1-5 seberapa mudah Anda melakukan Kinerja Harian?',
          ctaLabel: 'Kirim',
        }}
        formUrl="https://forms.gle/befhiN9NjPU1EvCc7"
        isOpen={isSurveyOpen}
        successContent={{
          title:
            'Ceritakan ke kami lebih lengkap saran dan masukan Anda untuk Kinerja Harian',
          ctaLabel: 'Isi Survei Kinerja Harian',
          ctaCloseLabel: 'x',
          getCountdownText: (secondsRemaining) =>
            `Dalam ${secondsRemaining} detik Anda akan diarahkan ke Survei Kinerja Harian`,
        }}
        onSubmit={handleSubmitSurvey}
        onClose={handleCloseSurveyDialog}
        onOpenFormUrl={handleOpenSurveyForm}
      />
    </main>
  )
}

function getDefaultSelectedMonthOption(
  selectedYear: number,
  currentDate = new Date()
) {
  if (selectedYear === currentDate.getFullYear()) {
    const currentMonth = MONTHS[currentDate.getMonth()]

    return `${currentMonth.toLowerCase()}-${selectedYear}`
  }

  return `${MONTHS[0].toLowerCase()}-${selectedYear}`
}
